<script setup lang="ts">
import { PropType, reactive, ref } from 'vue';
import { ModalToolbar } from 'md-editor-v3';
import type { Insert } from 'md-editor-v3';
import { Media, videoRules } from '@/models/media.model';

const data = reactive({
  modalVisible: false,
  modalFullscreen: false,
});

const props = defineProps({
  /**
   * `insert` will be automatically injected into the component by the editor
   */
  insert: {
    type: Function as PropType<Insert>,
    default: () => {
      //
    },
  },
  media: { type: Array as PropType<Media[]>, default: () => [] },
});

const imageUrl = ref<string>('');
const image = ref<Media | null>(null);
const size = ref<string>('small');
const videoUrl = ref<string>('');

const sizes = ['small', 'medium', 'large'];

const getSource = (m: Media) => {
  return m.file ? `/file/${m.file.uuid}/${size.value}.webp` : '';
};

const selectImage = (m: Media) => {
  image.value = image.value?.id === m.id ? null : m;
};

const selectSize = (s: string) => {
  size.value = s;
};

const handler = (type: string, url: string, title: string) => {
  // props.insert((selectedText) => {
  props.insert(() => {
    /**
     * targetValue    Content to be inserted
     * select         Automatically select content, default: true
     * deviationStart Start position of the selected content, default: 0
     * deviationEnd   End position of the selected content, default: 0
     */
    return {
      targetValue: `![${type}](${url}${title ? ` ${title}` : ''})`,
      select: true,
      deviationStart: 0,
      deviationEnd: 0,
    };
  });

  data.modalVisible = false;
};

const chooseImage = () => {
  if (imageUrl.value !== '') {
    handler('internet-image', imageUrl.value, '');
  } else if (image.value) {
    handler('shift-image', getSource(image.value), '');
  }
};
const tab = ref<string>('local-image');
const rotation = ref<string>('internet-video');

const chooseVideo = () => {
  if (videoUrl.value !== '') {
    handler(rotation.value, videoUrl.value, '');
    // } else if (image.value) {
    //   handler('shift-video', getSource(image.value), '');
  }
};
</script>
<template>
  <ModalToolbar
    :visible="data.modalVisible"
    :isFullscreen="data.modalFullscreen"
    title="media"
    modalTitle="Insert Media"
    width="600px"
    height="500px"
    @onClick="data.modalVisible = true"
    @onClose="data.modalVisible = false"
    @onAdjust="data.modalFullscreen = !data.modalFullscreen"
  >
    <v-tabs v-model="tab" fixed-tabs density="compact">
      <v-tab value="image">Image</v-tab>
      <v-tab value="video">Video</v-tab>
    </v-tabs>
    <v-tabs-window v-model="tab">
      <v-tabs-window-item value="image">
        <v-container>
          <h3>Insert an image into the text</h3>
          <p>Provide a url to an image</p>
          <v-text-field
            label="URL"
            v-model="imageUrl"
            placeholder="https://"
          ></v-text-field>
          <p>Or select an image from the attached media</p>
          <div class="media-list">
            <div
              v-for="m in media"
              :key="m.id"
              class="media-item"
              :class="{ 'media-item-selected': image?.id === m.id }"
              @click="selectImage(m)"
            >
              <img :src="getSource(m)" />
            </div>
          </div>
          <div v-if="image">
            <p>And select the size of the media to be used.</p>
            <div class="media-list">
              <div
                v-for="s in sizes"
                :key="s"
                class="media-item"
                :class="{ 'media-item-selected': size === s }"
                @click="selectSize(s)"
              >
                {{ s.substring(0, 1).toUpperCase() }}{{ s.substring(1) }}
              </div>
            </div>
          </div>
          <v-alert type="info" v-if="imageUrl && image"
            >The URL will be used, even though you have an image
            selected.</v-alert
          >
          <v-row>
            <v-col col="2"></v-col>
            <v-col>
              <v-btn
                class="mt-2"
                type="reset"
                block
                @click="data.modalVisible = false"
                >Cancel</v-btn
              >
            </v-col>
            <v-col>
              <v-btn class="mt-2" block type="button" @click="chooseImage"
                >Save</v-btn
              >
            </v-col>
            <v-col col="2"></v-col>
          </v-row>
        </v-container>
      </v-tabs-window-item>
      <v-tabs-window-item value="video">
        <h2>Use a video</h2>
        <v-container>
          <h3>Insert video into the text</h3>
          <p>Provide a url to a video</p>
          <v-text-field
            label="URL"
            v-model="videoUrl"
            :rules="videoRules"
            placeholder="https://"
          ></v-text-field>
          <v-radio-group v-model="rotation">
            <v-radio label="Landscape" value="internet-video"></v-radio>
            <v-radio label="Portrait" value="internet-portrait-video"></v-radio>
          </v-radio-group>

          <!-- <p>Or select an image from the attached media</p>
          <div class="media-list">
            <div
              v-for="m in media"
              :key="m.id"
              class="media-item"
              :class="{ 'media-item-selected': image?.id === m.id }"
              @click="selectImage(m)"
            >
              <img :src="getSource(m)" />
            </div>
          </div> -->
          <!-- <v-alert type="info" v-if="imageUrl && image"
            >The URL will be used, even though you have an image
            selected.</v-alert
          > -->
          <v-row>
            <v-col col="2"></v-col>
            <v-col>
              <v-btn
                class="mt-2"
                type="reset"
                block
                @click="data.modalVisible = false"
                >Cancel</v-btn
              >
            </v-col>
            <v-col>
              <v-btn class="mt-2" block type="button" @click="chooseVideo"
                >Save</v-btn
              >
            </v-col>
            <v-col col="2"></v-col>
          </v-row>
        </v-container>
      </v-tabs-window-item>
    </v-tabs-window>

    <template #trigger>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="lucide lucide-image-icon md-editor-icon"
      >
        <rect width="18" height="18" x="3" y="3" rx="2" ry="2"></rect>
        <circle cx="9" cy="9" r="2"></circle>
        <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path>
      </svg>
    </template>
  </ModalToolbar>
</template>
<style scoped>
.media-list {
  display: flex;
  flex-direction: row;
  margin-bottom: 8px;
}

.media-item {
  padding: 4px;
  margin-right: 4px;
  align-items: center;
  box-shadow: 2px 2px 4px gray;
  display: flex;
  border-radius: 4px;
  background-color: lightgray;
  min-width: 100px;
  min-height: 40px;
  text-align: center;
}
.media-item-selected {
  background-color: rgb(102, 189, 105);
}
.media-item img {
  min-width: 80px;
  max-width: 80px;
  max-height: 80px;
}
</style>
